import React, { useEffect, useMemo, useState, useRef } from 'react'
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner, Button } from 'reactstrap'
import TableContainer from '../../Components/Common/TableContainerReactTable.js';
import useObtenerCredencialesRdp from '../../Components/Hooks/useObtenerCredencialesRdp.js';
import useCreateCredencialRdp from '../../Components/Hooks/useCreateCredencialRdp.js';
import useUpdateCredencialRdp from '../../Components/Hooks/useUpdateCredencialRdp.js';
import useDeleteCredencialRdp from '../../Components/Hooks/useDeleteCredencialRdp.js';
import useImportCredencialesRdp from '../../Components/Hooks/useImportCredencialesRdp.js';
import ModalCrearCredencialRdp from '../../Components/modals/ModalCrearCredencialRdp.js';
import ModalModificarCredencialRdp from '../../Components/modals/ModalModificarCredencialRdp.js';
import ModalLeerCredencialRdp from '../../Components/modals/ModalLeerCredencialRdp.js';
import * as XLSX from 'xlsx';

const CredencialesRdp = () => {

    const [selectedCredencial, setSelectedCredencial] = useState(null);
    const [refresh, setRefresh] = useState(0);
    const { credenciales, loading, error } = useObtenerCredencialesRdp(refresh);
    const [modalCrearOpen, setModalCrearOpen] = useState(false);
    const [modalModificarOpen, setModalModificarOpen] = useState(false);
    const [modalLeerOpen, setModalLeerOpen] = useState(false);
    const { createCredencialRdp, loading: createLoading, error: createError } = useCreateCredencialRdp();
    const { updateCredencialRdp, loading: updateLoading, error: updateError } = useUpdateCredencialRdp();
    const { deleteCredencialRdp, loading: deleteLoading, error: deleteError } = useDeleteCredencialRdp();
    const { importExcel, loading: importLoading, error: importError, result: importResult } = useImportCredencialesRdp();
    const fileInputRef = useRef(null);

    useEffect(() => {
        document.title = "Credenciales RDP | Siga ERP";
    }, []);

    // Función para descargar plantilla Excel
    const downloadTemplate = () => {
        const templateData = [
            {
                username: 'usuario_ejemplo',
                password: 'contraseña123',
                hostname: '192.168.1.100',
                port: 3389,
                app_name: 'C:\\SIGAERP\\CLACLI.EXE',
                working_dir: 'C:\\SIGAERP\\'
            }
        ];

        const worksheet = XLSX.utils.json_to_sheet(templateData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Credenciales RDP');

        // Ajustar ancho de columnas
        worksheet['!cols'] = [
            { wch: 20 }, // username
            { wch: 20 }, // password
            { wch: 20 }, // hostname
            { wch: 10 }, // port
            { wch: 30 }, // app_name
            { wch: 40 }  // working_dir
        ];

        XLSX.writeFile(workbook, 'plantilla_credenciales_rdp.xlsx');
    };

    // Función para manejar la selección de archivo
    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const result = await importExcel(file);
            if (result) {
                let message = `Importación completada:\n`;
                message += `- Creados: ${result.created || 0}\n`;
                message += `- Actualizados: ${result.updated || 0}`;
                if (result.errors && result.errors.length > 0) {
                    message += `\n\nErrores:\n${result.errors.join('\n')}`;
                }
                alert(message);
                setRefresh(prev => prev + 1);
            }
        } catch (err) {
            alert('Error al importar: ' + (importError || err.message));
        }

        // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const columns = useMemo(
        () => [
            {
                header: "Username",
                accessorKey: "username",
                enableColumnFilter: false,
            },
            {
                header: "Hostname",
                accessorKey: "hostname",
                enableColumnFilter: false,
            },
            {
                header: "Puerto",
                accessorKey: "port",
                enableColumnFilter: false,
            },
            {
                header: "App Name",
                accessorKey: "app_name",
                enableColumnFilter: false,
                cell: ({ cell }) => cell.getValue() || '-',
            },
        ],
        []
    );

    const handleCellClick = (cellInfo) => {
        setSelectedCredencial(cellInfo.rowData);
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0">Credenciales RDP</h5>
                                    <div className="d-flex align-items-center">
                                        <Button color="success" className="me-2" onClick={downloadTemplate}>
                                            <i className="ri-file-excel-2-line me-1"></i>Descargar Plantilla
                                        </Button>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: 'none' }}
                                            accept=".xlsx,.xls"
                                            onChange={handleFileSelect}
                                        />
                                        <Button color="success" className="me-2" onClick={() => fileInputRef.current?.click()} disabled={importLoading}>
                                            {importLoading ? (
                                                <>
                                                    <Spinner size="sm" className="me-1" />Importando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-file-excel-line me-1"></i>Importar Excel
                                                </>
                                            )}
                                        </Button>
                                        <Button color="primary" className="me-2" onClick={() => setModalCrearOpen(true)} disabled={createLoading}>
                                            <i className="ri-add-line me-1"></i>Crear
                                        </Button>
                                        <Button color="info" className="me-2" onClick={() => { if (selectedCredencial) setModalLeerOpen(true); }} disabled={!selectedCredencial}>
                                            <i className="ri-eye-line me-1"></i>Ver
                                        </Button>
                                        <Button color="warning" className="me-2" onClick={() => { if (selectedCredencial) setModalModificarOpen(true) }} disabled={!selectedCredencial || updateLoading}>
                                            <i className="ri-edit-line me-1"></i>Modificar
                                        </Button>
                                        <Button color="danger" className="me-2" onClick={async () => {
                                            if (selectedCredencial && window.confirm('¿Está seguro que desea eliminar esta credencial RDP? Esta acción no se puede deshacer.')) {
                                                const result = await deleteCredencialRdp(selectedCredencial.id);
                                                if (result) {
                                                    alert('Credencial RDP eliminada');
                                                    setRefresh(prev => prev + 1);
                                                    setSelectedCredencial(null);
                                                } else {
                                                    alert('Error: No se pudo eliminar');
                                                }
                                            }
                                        }} disabled={!selectedCredencial || deleteLoading}>
                                            <i className="ri-delete-bin-line me-1"></i>Eliminar
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    {error ? <div className="alert alert-danger">{error}</div> : loading ? <div className="text-center"><Spinner animation="border" variant="primary" /></div> : <TableContainer
                                        columns={(columns || [])}
                                        data={credenciales?.records || []}
                                        customPageSize={15}
                                        isGlobalFilter={true}
                                        SearchPlaceholder='Buscar...'
                                        onCellClick={handleCellClick}
                                    />}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            <ModalCrearCredencialRdp
                isOpen={modalCrearOpen}
                toggle={() => setModalCrearOpen(false)}
                onSave={async (formData) => {
                    const result = await createCredencialRdp(formData);
                    if (result) {
                        alert('Credencial RDP creada exitosamente');
                        setModalCrearOpen(false);
                        setRefresh(prev => prev + 1);
                    } else {
                        alert('Error al crear credencial RDP: ' + createError);
                    }
                }}
            />
            <ModalModificarCredencialRdp
                isOpen={modalModificarOpen}
                toggle={() => setModalModificarOpen(false)}
                selectedCredencial={selectedCredencial}
                onSave={async (formData) => {
                    const result = await updateCredencialRdp(selectedCredencial.id, formData);
                    if (result) {
                        alert('Credencial RDP actualizada exitosamente');
                        setModalModificarOpen(false);
                        setRefresh(prev => prev + 1);
                    } else {
                        alert('Error al actualizar credencial RDP: ' + updateError);
                    }
                }}
            />
            <ModalLeerCredencialRdp
                isOpen={modalLeerOpen}
                toggle={() => setModalLeerOpen(false)}
                selectedCredencial={selectedCredencial}
            />
        </React.Fragment>
    )
}

export default CredencialesRdp;
